// Shared zone-aware date/time formatters, used by feature areas (Team, Charger,
// ...) that need to display timestamps in the currently selected header
// timezone instead of browser-local time. Deliberately kept separate from
// src/utils/formatTime.js, which remains browser-local for now — see
// docs/timezone-feature.md for the phased rollout plan.

const getParts = (date, timeZone, options) => {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, ...options }).formatToParts(date);
  return (type) => parts.find((p) => p.type === type)?.value;
};

// Zone-aware equivalent of formatTime.js's yearFirstDateTimeFormat
// ("yyyy-MM-dd hh:mm:ss a").
// Zone-aware equivalent of `dayjs(date).format("YYYY-MM-DD HH:mm")`
// (24-hour, no seconds).
export const isoDateTimeMinutesInTimezone = (date, timeZone) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";

  try {
    const get = getParts(parsed, timeZone, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}`;
  } catch (error) {
    return "";
  }
};

export const yearFirstDateTimeFormatInTimezone = (date, timeZone) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";

  try {
    const get = getParts(parsed, timeZone, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    const dayPeriod = (get("dayPeriod") || "").toUpperCase();
    return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}:${get("second")} ${dayPeriod}`;
  } catch (error) {
    return "";
  }
};

// Zone-aware equivalent of formatTime.js's onlyTime ("hh:mm:ss a").
export const onlyTimeInTimezone = (date, timeZone) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";

  try {
    const get = getParts(parsed, timeZone, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    const dayPeriod = (get("dayPeriod") || "").toUpperCase();
    return `${get("hour")}:${get("minute")}:${get("second")} ${dayPeriod}`;
  } catch (error) {
    return "";
  }
};

// Zone-aware equivalent of formatTime.js's fDateTime(date, "yyyy-MM-dd hh:mm a")
// (12-hour, no seconds).
export const dateHourMinuteFormatInTimezone = (date, timeZone) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";

  try {
    const get = getParts(parsed, timeZone, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    const dayPeriod = (get("dayPeriod") || "").toUpperCase();
    return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")} ${dayPeriod}`;
  } catch (error) {
    return "";
  }
};

// Zone-aware equivalent of formatTime.js's `fDateTime(date, "dd-MM-yyyy")`.
export const dayMonthYearFormatInTimezone = (date, timeZone) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";

  try {
    const get = getParts(parsed, timeZone, { year: "numeric", month: "2-digit", day: "2-digit" });
    return `${get("day")}-${get("month")}-${get("year")}`;
  } catch (error) {
    return "";
  }
};

// Zone-aware equivalent of formatTime.js's `fDateTime(date, "dd-MM-yyyy hh:mm:ss a")`.
export const dayMonthYearTimeFormatInTimezone = (date, timeZone) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";

  try {
    const get = getParts(parsed, timeZone, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    const dayPeriod = (get("dayPeriod") || "").toUpperCase();
    return `${get("day")}-${get("month")}-${get("year")} ${get("hour")}:${get("minute")}:${get("second")} ${dayPeriod}`;
  } catch (error) {
    return "";
  }
};

// Zone-aware equivalent of `new Date(dateStr).toLocaleString()`.
// Falls back to browser-local toLocaleString() when no timezone is available.
export const toLocaleStringInTimezone = (dateStr, timeZone) => {
  if (!dateStr) return "N/A";
  try {
    const parsed = new Date(dateStr);
    if (Number.isNaN(parsed.getTime())) return "N/A";
    return timeZone ? parsed.toLocaleString(undefined, { timeZone }) : parsed.toLocaleString();
  } catch (error) {
    return "N/A";
  }
};

// Zone-aware "yyyy-MM-dd" date-only formatter, matching the same yyyy-MM-dd
// convention as yearFirstDateTimeFormatInTimezone but without a time part.
export const isoDateOnlyInTimezone = (date, timeZone) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";

  try {
    const get = getParts(parsed, timeZone, { year: "numeric", month: "2-digit", day: "2-digit" });
    return `${get("year")}-${get("month")}-${get("day")}`;
  } catch (error) {
    return "";
  }
};

// Zone-aware equivalent of `new Date(dateStr).toLocaleDateString(...)`.
export const toLocaleDateStringInTimezone = (dateStr, timeZone, options = {}) => {
  if (!dateStr) return "N/A";
  try {
    const parsed = new Date(dateStr);
    if (Number.isNaN(parsed.getTime())) return "N/A";
    return timeZone
      ? parsed.toLocaleDateString(undefined, { ...options, timeZone })
      : parsed.toLocaleDateString(undefined, options);
  } catch (error) {
    return "N/A";
  }
};

// Zone-aware equivalent of formatTime.js's `f(date, "dd/MM/yyyy")`.
export const dateOnlyFormatInTimezone = (date, timeZone) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";

  try {
    const get = getParts(parsed, timeZone, { year: "numeric", month: "2-digit", day: "2-digit" });
    return `${get("day")}/${get("month")}/${get("year")}`;
  } catch (error) {
    return "";
  }
};

// Zone-aware equivalent of `format(date, "dd MMM yyyy, HH:mm")`.
export const dateTimeShortFormatInTimezone = (date, timeZone) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";

  try {
    const get = getParts(parsed, timeZone, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${get("day")} ${get("month")} ${get("year")}, ${get("hour")}:${get("minute")}`;
  } catch (error) {
    return "";
  }
};

// Returns the given zone's UTC offset (minutes, e.g. +330 for IST) at the
// instant `date` falls on — via Intl, so DST-observing zones resolve
// correctly for that specific date rather than a fixed offset assumption.
const offsetMinutesAt = (date, timeZone) => {
  const get = getParts(date, timeZone, { timeZoneName: "shortOffset", hour: "2-digit" });
  const offsetPart = get("timeZoneName") || "";
  const match = offsetPart.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/);
  if (!match) return 0;
  const sign = match[1] === "-" ? -1 : 1;
  return sign * (Number(match[2]) * 60 + Number(match[3] ?? 0));
};

// For editable date/time pickers (e.g. MUI DateTimePicker) that only edit
// plain browser-local `Date` objects with no timezone awareness: converts a
// UTC instant into a `Date` whose LOCAL wall-clock digits (as the browser
// would render them) match what the selected timezone's wall-clock shows for
// that instant. Feed this into a picker's `value` so what's displayed matches
// the selected zone rather than the browser's zone. Returns null for falsy input.
export const utcToPickerDate = (utcValue, timeZone) => {
  if (!utcValue) return null;
  const parsed = new Date(utcValue);
  if (Number.isNaN(parsed.getTime())) return null;
  if (!timeZone) return parsed;

  const get = getParts(parsed, timeZone, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  return new Date(
    Number(get("year")),
    Number(get("month")) - 1,
    Number(get("day")),
    Number(get("hour")),
    Number(get("minute")),
    Number(get("second"))
  );
};

// Inverse of utcToPickerDate: takes a picker's `Date` (whose local wall-clock
// digits represent a moment in the selected timezone, not the browser's zone)
// and returns the real UTC Date instant those digits correspond to. Returns
// null for falsy/invalid input.
export const pickerDateToUtc = (pickerValue, timeZone) => {
  if (!pickerValue) return null;
  const localDate = new Date(pickerValue);
  if (Number.isNaN(localDate.getTime())) return null;
  if (!timeZone) return localDate;

  // Build a UTC instant from the picker's displayed digits, then correct by
  // the selected zone's offset at that instant (iterate once since offset
  // can shift the date near a DST boundary — one correction pass is enough
  // for all real-world offsets).
  const naiveUtc = new Date(Date.UTC(
    localDate.getFullYear(),
    localDate.getMonth(),
    localDate.getDate(),
    localDate.getHours(),
    localDate.getMinutes(),
    localDate.getSeconds()
  ));
  const offsetMinutes = offsetMinutesAt(naiveUtc, timeZone);
  return new Date(naiveUtc.getTime() - offsetMinutes * 60000);
};
